// src/components/search/RecentSearchesList.tsx
import {
  Box,
  Chip,
  Divider,
  IconButton,
  List,
  ListItemButton,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import type { RecentSearch } from "@/search/types";
import { parseSearchParams } from "@/search/utils";

interface RecentSearchesListProps {
  searches: RecentSearch[];
  isLoading: boolean;
  hasData: boolean;
  expandedIds: string[];
  onToggleExpanded: (id: string) => void;
  onSelectSearch: (search: RecentSearch) => void;
  onPinSearch: (id: string) => void;
  onUnpinSearch: (id: string) => void;
}

export default function RecentSearchesList({
  searches,
  isLoading,
  hasData,
  expandedIds,
  onToggleExpanded,
  onSelectSearch,
  onPinSearch,
  onUnpinSearch,
}: RecentSearchesListProps) {
  // Derive pinned/unpinned internally
  const pinnedSearches = searches.filter((s) => s.pinned);
  const unpinnedSearches = searches.filter((s) => !s.pinned);
  const allSearches = [...pinnedSearches, ...unpinnedSearches];
  const showSkeleton = isLoading && !hasData;

  return (
    <Box sx={{ px: 2, pb: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>
        Recent searches
      </Typography>

      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          borderRadius: 1,
        }}
      >
        {showSkeleton && (
          <Box sx={{ p: 1.5 }}>
            {[1, 2, 3].map((i) => (
              <Box key={i} sx={{ mb: i === 3 ? 0 : 1.2 }}>
                <Skeleton variant="rounded" height={56} />
              </Box>
            ))}
          </Box>
        )}

        {!showSkeleton && hasData && (
          <List disablePadding>
            {allSearches.map((entry, idx, arr) => (
              <RecentSearchItem
                key={entry.id}
                entry={entry}
                isLast={idx === arr.length - 1}
                isExpanded={expandedIds.includes(entry.id)}
                onToggleExpanded={() => onToggleExpanded(entry.id)}
                onSelect={() => onSelectSearch(entry)}
                onPin={() => onPinSearch(entry.id)}
                onUnpin={() => onUnpinSearch(entry.id)}
              />
            ))}
          </List>
        )}
      </Box>
    </Box>
  );
}

interface RecentSearchItemProps {
  entry: RecentSearch;
  isLast: boolean;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onSelect: () => void;
  onPin: () => void;
  onUnpin: () => void;
}

function RecentSearchItem({
  entry,
  isLast,
  isExpanded,
  onToggleExpanded,
  onSelect,
  onPin,
  onUnpin,
}: RecentSearchItemProps) {
  const { street, postcodes, statuses } = parseSearchParams(entry.params);
  const hasExtraPostcodes = postcodes.length > 1;

  const postcodeDisplay = (() => {
    if (postcodes.length === 0) return "";
    if (isExpanded) return `Postcode: ${postcodes.join(", ")}`;
    const [first, ...rest] = postcodes;
    if (rest.length === 0) return `Postcode: ${first}`;
    return `Postcode: ${first}, +${rest.length} more`;
  })();

  return (
    <Box>
      <ListItemButton
        alignItems="flex-start"
        onClick={onSelect}
        sx={{
          display: "flex",
          gap: 1.5,
          alignItems: "flex-start",
          p: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {street}
            </Typography>
            {entry.pinned && (
              <Chip
                size="small"
                label="Pinned"
                color="primary"
                variant="outlined"
              />
            )}
          </Stack>

          <Stack spacing={0.25}>
            {postcodeDisplay && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ lineHeight: 1.4 }}
              >
                {postcodeDisplay}
              </Typography>
            )}
            {statuses.length > 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ lineHeight: 1.4 }}
              >
                {`Status: ${statuses.join(", ")}`}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              {`${entry.result_count ?? 0} results`}
            </Typography>
          </Stack>
        </Stack>

        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{ minWidth: 88, justifyContent: "flex-end" }}
        >
          <IconButton
            size="small"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (entry.pinned) onUnpin();
              else onPin();
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            {entry.pinned ? (
              <PushPinIcon fontSize="small" />
            ) : (
              <PushPinOutlinedIcon fontSize="small" />
            )}
          </IconButton>

          {hasExtraPostcodes ? (
            <IconButton
              size="small"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onToggleExpanded();
              }}
              aria-label={isExpanded ? "Collapse postcodes" : "Expand postcodes"}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              {isExpanded ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </IconButton>
          ) : (
            <Box sx={{ width: 32, height: 32 }} />
          )}
        </Stack>
      </ListItemButton>

      {!isLast && <Divider />}
    </Box>
  );
}
